import { graphql, useStaticQuery } from 'gatsby';
import React, { Suspense, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Divider } from '../components/Divider';
import { LinkList } from '../components/LinkList';
import { Loader } from '../components/Loader';
import { Section } from '../components/Section';
import { SEO } from '../components/Seo';
import { Spacer } from '../components/Spacer';
import { Typography } from '../components/Typography';
import { VacancyNotFound } from '../components/VacancyNotFound';
import { LocationAction404 } from '../components/locations/LocationAction404';
import { LocationActionSection } from '../components/locations/LocationActionSection';
import { SimplePageHeader } from '../components/pageHeader/SimplePageHeader';
import { useHasMounted } from '../hooks/useHasMounted';
import { usePropertyByRefLazyQuery } from '../types/graphqlHelpers';
import { useDataLayerPush } from '../hooks/useDataLayerPush';
import { getLastItem, isPropertyPageCheck } from '../utils';
import { getCleanPropertyDetails } from '../utils/getCleanPropertyDetails';

const PropertyDetails = React.lazy(() => import('../components/properties/PropertyDetails'));
const StyledNav = styled.nav`
  ${({ theme: { space } }) => css`
    display: flex;
    max-width: 348px;
    grid-template-columns: 1fr 1fr;
    gap: ${space.lg};
    justify-content: space-between;
  `};
`;

const numberPattern = /[^/](\w+)[^-|/]/gi;
const NotFoundPage = ({ location }) => {
  const { pathname } = location;
  const { dataLayerPush } = useDataLayerPush();

  // if path like this /careers/lettings-negotiator-belsize-park-18394577/ then show careers message
  const isCareersPage = pathname.startsWith('/careers/');

  const isPropertyPage = isPropertyPageCheck(pathname as string);

  const [getProperty, { loading, error, data: property, called }] = usePropertyByRefLazyQuery();
  const hasMounted = useHasMounted();
  // need p
  // not d
  // before -
  useEffect(() => {
    if (isPropertyPage) {
      const pathToTest = getLastItem(pathname as string);

      let ref;

      if (pathToTest.toLowerCase().startsWith('k')) {
        ref = pathToTest;
      } else {
        const matches = pathToTest.match(numberPattern);
        if (matches && matches.length > 0) {
          ref = matches[0].replace('d', '');
        } else {
          ref = '';
        }
      }

      getProperty({ variables: { ref } });
    }
  }, [getProperty, isPropertyPage, pathname]);

  const { notFoundData } = useStaticQuery(graphql`
    query notFoundQuery {
      notFoundData: kfhContentGlobalSettingsUmbracoType {
        notFound {
          heading
          lead
          linkLists {
            items {
              link {
                name
                url
              }
            }
          }
        }
      }
    }
  `);

  const { notFound } = notFoundData;

  // Handle data layer for property 404

  useEffect(() => {
    if (isPropertyPage) {
      if (property?.properties?.propertyByRef) {
        // If the property is found, push the details to the data layer
        dataLayerPush({
          event: 'gatsby-route-change',
          contentGroup: 'property page',
          targetAudience: undefined,
          targetService: undefined,
          propertyDetails: getCleanPropertyDetails(property?.properties?.propertyByRef),
          internalTraffic: undefined,
        });
      } else if (called && !loading && !property?.properties?.propertyByRef) {
        // If it's a property page but the property couldn't be found, push this to the data layer
        dataLayerPush({
          event: 'gatsby-route-change',
          contentGroup: 'property page not available',
          targetAudience: undefined,
          targetService: undefined,
          propertyDetails: undefined,
          internalTraffic: undefined,
        });
      }
    }
  }, [property, called, loading, isPropertyPage, dataLayerPush]);

  if (isCareersPage) {
    return (
      <>
        <SimplePageHeader title="Sorry, this vacancy is no longer available" />
        <VacancyNotFound />
      </>
    );
  }

  return (
    <Suspense fallback={<Loader />}>
      <>
        {!hasMounted ||
          (loading && (
            <Section contained>
              <Loader />
            </Section>
          ))}

        {(called && !loading && hasMounted && property?.properties && !property?.properties?.propertyByRef) ||
        (!loading && hasMounted && !isPropertyPage && !property) ? (
          <>
            <SimplePageHeader
              title={isPropertyPage ? notFound.heading.replace('page', 'property') : notFound.heading}
            />
            {isPropertyPage && (
              <>
                <LocationAction404 />
                <Section contained>
                  <Divider />
                </Section>
              </>
            )}
            <Section contained background={isPropertyPage ? 'gradient' : 'white'}>
              <Spacer>
                <Typography component="p" variant="lead">
                  {notFound.lead}
                </Typography>
                <StyledNav>
                  {notFound.linkLists.map(({ items }, index) => (
                    <LinkList key={index} items={items} />
                  ))}
                </StyledNav>
              </Spacer>
            </Section>
            {!isPropertyPage && <LocationActionSection />}
          </>
        ) : (
          ''
        )}

        {called && !loading && hasMounted && property?.properties?.propertyByRef && (
          <PropertyDetails data={{ property: property.properties.propertyByRef }} location={location} />
        )}
      </>
    </Suspense>
  );
};

export default NotFoundPage;

export const Head = ({ location }) => (
  <SEO
    title={`${isPropertyPageCheck(location?.pathname as string) ? 'Property Details' : 'KFH | Sorry, page not found'}`}
  />
);
